// premium-contact: two-path contact with functional form and plan preselection
import React, { useState, useEffect } from 'react';
import { Navigation } from '@/components/navigation';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/footer';
import { ArrowRight, Calendar, Send, CheckCircle } from 'lucide-react';
import { SEO } from '@/components/seo';

const Contact = () => {
  // Get URL params to preselect plan
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedPlan = urlParams.get('plan');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    plan: preselectedPlan || '',
    timeline: '',
    goals: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Set the preselected plan when component mounts
  useEffect(() => {
    if (preselectedPlan) {
      setFormData(prev => ({ ...prev, plan: preselectedPlan }));
    }
  }, [preselectedPlan]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Integrate with actual form handler
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getPlanName = (planValue: string) => {
    switch(planValue) {
      case 'Website Essentials': return 'Website Essentials ($500/year)';
      case 'Growth Website + SEO': return 'Growth Website + SEO ($800/year)';
      case 'AI Chatbot': return 'AI Chatbot ($500/year)';
      case 'Website + SEO + Chatbot': return 'Website + SEO + Chatbot ($1,000/year)';
      case 'Custom App / Web App / MVP': return 'Custom App / Web App / MVP (Custom)';
      default: return 'Select plan';
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <section className="pt-32 pb-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              <div className="glass-panel p-12 max-w-lg mx-auto">
                <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
                <h1 className="font-display font-semibold text-foreground mb-4" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                  We'll be in touch soon
                </h1>
                <p className="text-muted-foreground mb-6">
                  Thanks—We'll reply within 24 hours from post@affinityaim.com.
                </p>
                <Button 
                  variant="ghost" 
                  onClick={() => setIsSubmitted(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Send another message
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Contact Us - Start Your Web Development Project | AffinityAim"
        description="Contact AffinityAim to discuss your web development project. Free consultation, fast response. Email: post@affinityaim.com"
        canonical="https://www.affinityaim.com/contact"
        keywords="contact AffinityAim, web development consultation, get a quote"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact AffinityAim"
        }}
      />
      <Navigation />

      <main id="main-content">
        {/* Hero */}
        <section className="section-spacing bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <div className="mb-16">
                <div className="micro-label mb-6">CONTACT</div>
                <h1 className="font-display font-bold text-foreground mb-6" style={{ fontSize: '56px', lineHeight: '1.1', letterSpacing: '-0.01em' }}>
                  Let's discuss
                  <br />
                  <span className="bg-gradient-primary bg-clip-text text-transparent">
                    your project
                  </span>
                </h1>
                <p className="text-body-lg text-muted-foreground max-w-paragraph mx-auto">
                  Choose the best way to start: book a quick call or send us your project details.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Two-Path Contact */}
        <section className="pb-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content-wide mx-auto">
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                
                {/* Path 1: Book a Call */}
                <div className="card-matte p-8">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="font-display font-semibold text-foreground mb-4" style={{ fontSize: '24px', lineHeight: '1.3' }}>
                      Book a 15-minute call
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Quick chat to understand your goals and see if we're a good fit.
                    </p>
                    
                    {/* Calendar placeholder */}
                    <div className="bg-background-secondary rounded-lg p-8 mb-6 border border-stroke-hairline">
                      <div className="text-center text-muted-foreground">
                        <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="mb-4">Calendar integration coming soon</p>
                        <p className="text-sm">For now, email us to schedule: <a href="mailto:post@affinityaim.com" className="text-primary hover:underline">post@affinityaim.com</a></p>
                      </div>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      <p>• No pressure—15 minutes</p>
                      <p>• Discuss timeline and approach</p>
                      <p>• Get a rough estimate</p>
                    </div>
                  </div>
                </div>

                {/* Path 2: Send Details */}
                <div className="card-matte p-8">
                  <div className="mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                      <Send className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="font-display font-semibold text-foreground mb-4" style={{ fontSize: '24px', lineHeight: '1.3' }}>
                      Send us your details
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Share your project info and we'll respond within 24 hours.
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-name">
                          Name *
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-4 py-3 bg-background border border-stroke-hairline rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 tap-target"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-email">
                          Email *
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-4 py-3 bg-background border border-stroke-hairline rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 tap-target"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-company">
                        Company
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="w-full px-4 py-3 bg-background border border-stroke-hairline rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 tap-target"
                        placeholder="Company name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-plan">
                          Which plan interests you?
                        </label>
                        <select
                          id="contact-plan"
                          value={formData.plan}
                          onChange={(e) => handleInputChange('plan', e.target.value)}
                          className="w-full px-4 py-3 bg-background border border-stroke-hairline rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 tap-target"
                        >
                          <option value="">Select plan</option>
                          <option value="Website Essentials">Website Essentials ($500/year)</option>
                          <option value="Growth Website + SEO">Growth Website + SEO ($800/year)</option>
                          <option value="AI Chatbot">AI Chatbot ($500/year)</option>
                          <option value="Website + SEO + Chatbot">Website + SEO + Chatbot ($1,000/year)</option>
                          <option value="Custom App / Web App / MVP">Custom App / Web App / MVP (Custom)</option>
                          <option value="help-decide">Help me decide</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-timeline">
                          Timeline
                        </label>
                        <select
                          id="contact-timeline"
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="w-full px-4 py-3 bg-background border border-stroke-hairline rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 tap-target"
                        >
                          <option value="">Select timeline</option>
                          <option value="asap">ASAP</option>
                          <option value="1-2-months">1-2 months</option>
                          <option value="3-6-months">3-6 months</option>
                          <option value="6+-months">6+ months</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2" htmlFor="contact-goals">
                        What are your goals? *
                      </label>
                      <textarea
                        id="contact-goals"
                        required
                        rows={4}
                        value={formData.goals}
                        onChange={(e) => handleInputChange('goals', e.target.value)}
                        className="w-full px-4 py-3 bg-background border border-stroke-hairline rounded-lg text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none tap-target"
                        placeholder="Tell us about your project goals, challenges, or what you'd like to build..."
                      />
                    </div>

                    <Button 
                      type="submit"
                      variant="default" 
                      size="lg" 
                      className="w-full group bg-primary hover:bg-primary/90 text-primary-foreground tap-target"
                    >
                      Send details
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    <p className="text-sm text-muted-foreground text-center">
                      We'll reply within 24 hours from post@affinityaim.com.
                    </p>

                  </form>
                </div>

              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;